import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  Fragment,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import {
  dailyImageUrl,
  dateKeyForOffset,
  formatDailyLabel,
  formatHigherLowerValue,
  higherLowerPickWins,
  higherLowerRounds,
  higherLowerScore,
  isDailyHigherLower,
  isDailyJoin,
  isDailyOddOneOut,
  joinAnswerMask,
  joinIsCorrect,
  isDailyOrganize,
  isDailyPinpoint,
  isDailyQuiz,
  isDailySort,
  isDailyTrip,
  isDailyWhereIs,
  offsetToIndex,
  oddOneOutAnswer,
  organizeIsSolved,
  organizeLabel,
  organizeMove,
  organizeOrderFits,
  organizeSolution,
  pinpointTarget,
  quizFieldAnswers,
  shuffleOddOneOut,
  shuffleOrganize,
  shuffleSort,
  sortCorrectCount,
  sortIsSolved,
  sortPlacementFits,
  sortSolution,
  todayOffset,
  tripAllSolved,
  tripBlankIndices,
  tripIsGiven,
  tripRouteLabel,
  tripSegmentKm,
  whereIsArrows,
  JOIN_HINT_COUNT,
  PINPOINT_DIFFICULTIES,
  PINPOINT_HIT_KM,
  QUIZ_DIFFICULTIES,
  type DailyEntry,
  type DailyHigherLower,
  type DailyHigherLowerOption,
  type DailyJoin,
  type DailyOddOneOut,
  type DailyOrganize,
  type DailyPinpoint,
  type DailyPlace,
  type DailyQuiz,
  type DailySort,
  type DailyTrip,
  type DailyWhereIs,
  type PinpointDifficulty,
  type QuizDifficulty,
  type WhereIsArrow,
} from "../lib/daily";
import { celebrateDailyWin } from "../lib/confetti";
import {
  compassLabelCs,
  formatDistance,
  formatKm,
  haversineKm,
  initialBearingDeg,
  type LatLng,
} from "../lib/geo";
import { normalize } from "../lib/normalize";
import {
  emptyHigherLowerProgress,
  emptyOddOneOutProgress,
  emptyOrganizeProgress,
  emptyPinpointProgress,
  emptySortProgress,
  emptyTripProgress,
  getDailyDayProgress,
  loadDailyProgress,
  saveDailyProgress,
  type DailyDayProgress,
  type DailyProgressStore,
  type DailyWhereIsProgress,
} from "../lib/persistence";
import type { DailyState } from "../lib/useDaily";
import { PinpointMap } from "./PinpointMap";

interface DailyViewProps {
  dailyState: DailyState;
  offset: number;
  onOffsetChange: (offset: number) => void;
  onSolved?: () => void;
  /**
   * Hidden dev/testing mode (`/daily-test/:id`): `offset` is treated as
   * a raw index into `places` rather than a calendar day, navigation is
   * not capped at "today", and progress is stored under a separate key
   * so it never collides with real play data.
   */
  testMode?: boolean;
}

const GIVE_UP_CONFIRM = "Opravdu se chceš vzdát? Zobrazí se ti správná odpověď a dnešní hádanka se bude počítat jako nevyřešená.";

const QUIZ_TIER_LABEL: Record<QuizDifficulty, string> = {
  easy: "Lehká",
  medium: "Střední",
  hard: "Těžká",
};

const PINPOINT_TIER_LABEL: Record<PinpointDifficulty, string> = {
  easy: "Lehčí",
  harder: "Těžší",
};

export function DailyView({
  dailyState,
  offset,
  onOffsetChange,
  onSolved,
  testMode = false,
}: DailyViewProps) {
  const [progress, setProgress] = useState<DailyProgressStore>(() => loadDailyProgress());

  const today = todayOffset();
  const placesCount = dailyState.status === "ready" ? dailyState.data.places.length : 0;
  const dateKey = testMode ? `test:${offset}` : dateKeyForOffset(offset);
  const dayProgress = getDailyDayProgress(progress, dateKey);
  const solved = dayProgress.solved;

  const entry: DailyEntry | null = useMemo(() => {
    if (dailyState.status !== "ready") return null;
    const idx = offsetToIndex(offset, dailyState.data.places.length);
    return dailyState.data.places[idx] ?? null;
  }, [dailyState, offset]);

  const imageIndex = useMemo(() => {
    if (dailyState.status !== "ready") return 0;
    return offsetToIndex(offset, dailyState.data.places.length);
  }, [dailyState, offset]);

  const persistDay = useCallback(
    (patch: Partial<DailyDayProgress>) => {
      setProgress((prev) => {
        const next = {
          ...prev,
          [dateKey]: { ...getDailyDayProgress(prev, dateKey), ...patch },
        };
        saveDailyProgress(next);
        return next;
      });
      if (patch.solved || patch.givenUp) onSolved?.();
    },
    [dateKey, onSolved],
  );

  const canGoOlder = offset > 0;
  const canGoNewer = testMode ? offset < placesCount - 1 : offset < today;

  if (dailyState.status === "loading") {
    return (
      <main className="daily-view">
        <div className="daily-status">Načítám denní hádanku…</div>
      </main>
    );
  }

  if (dailyState.status === "error") {
    return (
      <main className="daily-view">
        <div className="daily-status daily-status-error">
          Chyba při načítání: {dailyState.message}
        </div>
      </main>
    );
  }

  if (!entry) {
    return (
      <main className="daily-view">
        <div className="daily-status">Žádná hádanka pro tento den.</div>
      </main>
    );
  }

  return (
    <main className="daily-view">
      <div className="daily-card">
        {testMode && <div className="daily-test-banner">Testovací režim · index {offset}</div>}
        <nav className="daily-nav" aria-label="Výběr dne">
          <button
            type="button"
            className="daily-nav-btn"
            onClick={() => onOffsetChange(offset - 1)}
            disabled={!canGoOlder}
            aria-label="Předchozí den"
          >
            ‹
          </button>
          <span className="daily-date">
            {testMode ? `Záznam #${offset}` : formatDailyLabel(offset)}
          </span>
          <button
            type="button"
            className="daily-nav-btn"
            onClick={() => onOffsetChange(offset + 1)}
            disabled={!canGoNewer}
            aria-label="Další den"
          >
            ›
          </button>
        </nav>

        {isDailyQuiz(entry) ? (
          <QuizCard
            quiz={entry}
            dayProgress={dayProgress}
            solved={solved}
            persistDay={persistDay}
          />
        ) : isDailyWhereIs(entry) ? (
          <WhereIsCard
            entry={entry}
            dayProgress={dayProgress}
            solved={solved}
            persistDay={persistDay}
            resetKey={`${offset}-${testMode}`}
          />
        ) : isDailyPinpoint(entry) ? (
          <PinpointCard
            entry={entry}
            dayProgress={dayProgress}
            solved={solved}
            persistDay={persistDay}
            resetKey={`${offset}-${testMode}`}
          />
        ) : isDailyOddOneOut(entry) ? (
          <OddOneOutCard
            entry={entry}
            dayProgress={dayProgress}
            solved={solved}
            persistDay={persistDay}
            shuffleSeed={dateKey}
          />
        ) : isDailyHigherLower(entry) ? (
          <HigherLowerCard
            // Remount on day change so the review step doesn't carry over.
            key={`${offset}-${testMode}`}
            entry={entry}
            dayProgress={dayProgress}
            solved={solved}
            persistDay={persistDay}
          />
        ) : isDailyOrganize(entry) ? (
          <OrganizeCard
            // Remount on day change so the last verdict doesn't carry over.
            key={`${offset}-${testMode}`}
            entry={entry}
            dayProgress={dayProgress}
            solved={solved}
            persistDay={persistDay}
            shuffleSeed={dateKey}
          />
        ) : isDailyTrip(entry) ? (
          <TripCard
            // Remount on day change so focused blanks and shake state don't carry over.
            key={`${offset}-${testMode}`}
            entry={entry}
            dayProgress={dayProgress}
            solved={solved}
            persistDay={persistDay}
            resetKey={`${offset}-${testMode}`}
          />
        ) : isDailySort(entry) ? (
          <SortCard
            // Remount on day change so a selection or verdict doesn't carry over.
            key={`${offset}-${testMode}`}
            entry={entry}
            dayProgress={dayProgress}
            solved={solved}
            persistDay={persistDay}
            shuffleSeed={dateKey}
          />
        ) : isDailyJoin(entry) ? (
          <JoinCard
            entry={entry}
            dayProgress={dayProgress}
            solved={solved}
            persistDay={persistDay}
            resetKey={`${offset}-${testMode}`}
          />
        ) : (
          <PlaceCard
            place={entry}
            imageIndex={imageIndex}
            dayProgress={dayProgress}
            solved={solved}
            persistDay={persistDay}
            resetKey={`${offset}-${testMode}`}
          />
        )}
      </div>
    </main>
  );
}

interface PlaceCardProps {
  place: DailyPlace;
  imageIndex: number;
  dayProgress: DailyDayProgress;
  solved: boolean;
  persistDay: (patch: Partial<DailyDayProgress>) => void;
  resetKey: string;
}

function PlaceCard({ place, imageIndex, dayProgress, solved, persistDay, resetKey }: PlaceCardProps) {
  const [input, setInput] = useState("");
  const [bad, setBad] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const givenUp = dayProgress.givenUp;
  const finished = solved || givenUp;

  useEffect(() => {
    setInput("");
    setBad(false);
    inputRef.current?.focus();
  }, [resetKey, finished]);

  const submit = useCallback(
    (value: string) => {
      if (finished) return;
      const key = normalize(value);
      if (!key) return;
      if (normalize(place.name) === key) {
        persistDay({ solved: true });
        celebrateDailyWin();
        setInput("");
        return;
      }
      setBad(true);
      setTimeout(() => setBad(false), 380);
    },
    [place, finished, persistDay],
  );

  const revealHint = useCallback(() => {
    if (finished) return;
    const max = place.hints.length;
    if (dayProgress.hintsRevealed >= max) return;
    persistDay({ hintsRevealed: dayProgress.hintsRevealed + 1 });
  }, [place, finished, dayProgress.hintsRevealed, persistDay]);

  const giveUp = useCallback(() => {
    if (finished) return;
    if (!window.confirm(GIVE_UP_CONFIRM)) return;
    persistDay({ givenUp: true });
    setInput("");
  }, [finished, persistDay]);

  const hintsShown = place.hints.slice(0, dayProgress.hintsRevealed);
  const hintsLeft = place.hints.length - dayProgress.hintsRevealed;

  return (
    <>
      <figure className="daily-figure">
        <img
          src={dailyImageUrl(imageIndex)}
          alt="Fotografie města k uhádnutí"
          className="daily-image"
        />
        <figcaption className="daily-credit">
          Foto: {place.img_author} ·{" "}
          <a href={place.img_license.url} target="_blank" rel="noopener noreferrer">
            {place.img_license.name}
          </a>
        </figcaption>
      </figure>

      {finished ? (
        <div className={"daily-result" + (givenUp ? " daily-result-lost" : "")}>
          {givenUp && <div className="daily-result-tag">Vzdáno</div>}
          <div className="daily-result-name">{place.name}</div>
          <p className="daily-result-desc">{place.description}</p>
        </div>
      ) : (
        <>
          <form
            className="daily-input-row"
            onSubmit={(e) => {
              e.preventDefault();
              submit(input);
            }}
          >
            <input
              ref={inputRef}
              className={"input" + (bad ? " input-bad" : "")}
              placeholder="Napiš název města…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="words"
              spellCheck={false}
              aria-label="Odpověď"
            />
          </form>

          {hintsShown.length > 0 && (
            <ul className="daily-hints">
              {hintsShown.map((hint, i) => (
                <li key={i}>{hint}</li>
              ))}
            </ul>
          )}

          <div className="daily-actions">
            {hintsLeft > 0 && (
              <button type="button" className="btn daily-hint-btn" onClick={revealHint}>
                Zobrazit nápovědu ({hintsLeft})
              </button>
            )}
            <button type="button" className="btn btn-danger daily-give-up-btn" onClick={giveUp}>
              Vzdát se
            </button>
          </div>
        </>
      )}
    </>
  );
}

interface QuizCardProps {
  quiz: DailyQuiz;
  dayProgress: DailyDayProgress;
  solved: boolean;
  persistDay: (patch: Partial<DailyDayProgress>) => void;
}

function QuizCard({ quiz, dayProgress, solved, persistDay }: QuizCardProps) {
  const quizProgress = dayProgress.quiz ?? { solved: {}, hintsRevealed: {} };
  const givenUp = dayProgress.givenUp;
  const finished = solved || givenUp;

  const answerField = useCallback(
    (tier: QuizDifficulty, value: string) => {
      if (finished || quizProgress.solved[tier]) return;
      const key = normalize(value);
      if (!key) return false;
      const accepted = quizFieldAnswers(quiz.answers[tier]);
      if (!accepted.some((a) => normalize(a) === key)) return false;

      const nextSolved = { ...quizProgress.solved, [tier]: true };
      const allSolved = QUIZ_DIFFICULTIES.every((t) => nextSolved[t]);
      persistDay({
        quiz: { ...quizProgress, solved: nextSolved },
        ...(allSolved ? { solved: true } : {}),
      });
      if (allSolved) celebrateDailyWin();
      return true;
    },
    [quiz, quizProgress, finished, persistDay],
  );

  const revealHint = useCallback(
    (tier: QuizDifficulty) => {
      if (finished || quizProgress.solved[tier] || quizProgress.hintsRevealed[tier]) return;
      persistDay({
        quiz: { ...quizProgress, hintsRevealed: { ...quizProgress.hintsRevealed, [tier]: true } },
      });
    },
    [quizProgress, finished, persistDay],
  );

  const giveUp = useCallback(() => {
    if (finished) return;
    if (!window.confirm(GIVE_UP_CONFIRM)) return;
    persistDay({ givenUp: true });
  }, [finished, persistDay]);

  return (
    <div className="daily-quiz">
      <p className="daily-quiz-question">{quiz.question}</p>

      <div className="daily-quiz-fields">
        {QUIZ_DIFFICULTIES.map((tier, i) => (
          <QuizField
            key={tier}
            index={i + 1}
            tier={tier}
            field={quiz.answers[tier]}
            correct={Boolean(quizProgress.solved[tier])}
            revealed={solved || givenUp || Boolean(quizProgress.solved[tier])}
            hintRevealed={Boolean(quizProgress.hintsRevealed[tier])}
            onAnswer={(value) => answerField(tier, value)}
            onRevealHint={() => revealHint(tier)}
          />
        ))}
      </div>

      {!finished && (
        <button type="button" className="btn btn-danger daily-give-up-btn" onClick={giveUp}>
          Vzdát se
        </button>
      )}
    </div>
  );
}

interface QuizFieldProps {
  index: number;
  tier: QuizDifficulty;
  field: DailyQuiz["answers"][QuizDifficulty];
  /** Answered correctly by the player. */
  correct: boolean;
  /** Answer is shown, either because it's correct or the day was given up. */
  revealed: boolean;
  hintRevealed: boolean;
  onAnswer: (value: string) => boolean;
  onRevealHint: () => void;
}

function QuizField({
  index,
  tier,
  field,
  correct,
  revealed,
  hintRevealed,
  onAnswer,
  onRevealHint,
}: QuizFieldProps) {
  const [input, setInput] = useState("");
  const [bad, setBad] = useState(false);

  const submit = () => {
    if (revealed) return;
    if (!input.trim()) return;
    const ok = onAnswer(input);
    if (ok) {
      setInput("");
    } else {
      setBad(true);
      setTimeout(() => setBad(false), 380);
    }
  };

  const canonicalAnswer = quizFieldAnswers(field)[0] ?? "";

  return (
    <div
      className={
        "daily-quiz-field" + (correct ? " is-answered" : revealed ? " is-givenup" : "")
      }
    >
      <div className="daily-quiz-field-head">
        <span className="daily-quiz-field-index">({index})</span>
        <span className="daily-quiz-field-tier">{QUIZ_TIER_LABEL[tier]}</span>
      </div>

      {revealed ? (
        <>
          <div className="daily-quiz-field-answer">{canonicalAnswer}</div>
          <p className="daily-quiz-field-hint">{field.hint}</p>
        </>
      ) : (
        <>
          <form
            className="daily-input-row"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <input
              className={"input" + (bad ? " input-bad" : "")}
              placeholder="Tvoje odpověď…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="words"
              spellCheck={false}
              aria-label={`Odpověď (${index})`}
            />
          </form>

          {hintRevealed ? (
            <ul className="daily-hints">
              <li>{field.hint}</li>
            </ul>
          ) : (
            <button type="button" className="btn daily-hint-btn" onClick={onRevealHint}>
              Zobrazit nápovědu
            </button>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Where-is diagram geometry, in the units of the 100×100 `viewBox`. The
 * label radius plus the label's own max-width (see `.daily-whereis-label`)
 * has to stay under 50 so the outermost labels don't spill out of the box.
 */
const WHEREIS_CENTER = 50;
const WHEREIS_ARROW_START = 10.5;
const WHEREIS_HEAD_LEN = 3.6;
const WHEREIS_HEAD_HALF = 1.9;
const WHEREIS_LABEL_RADIUS = 24.5;
/** Shortest label radius used when neighbouring arrows have to be fanned out. */
const WHEREIS_LABEL_RADIUS_MIN = 16.5;
/** Distance between the arrow tip and its label. */
const WHEREIS_LABEL_GAP = 1;

interface WhereIsGeometry {
  line: { x1: string; y1: string; x2: string; y2: string };
  head: string;
}

const r2 = (n: number) => n.toFixed(2);

/** Unit vector of a bearing in SVG space (y grows downwards). */
function whereIsUnit(bearing: number): { ux: number; uy: number } {
  const rad = (bearing * Math.PI) / 180;
  return { ux: Math.sin(rad), uy: -Math.cos(rad) };
}

function whereIsGeometry(bearing: number, labelRadius: number): WhereIsGeometry {
  const { ux, uy } = whereIsUnit(bearing);
  const tip = labelRadius - WHEREIS_LABEL_GAP;
  const tipX = WHEREIS_CENTER + tip * ux;
  const tipY = WHEREIS_CENTER + tip * uy;
  const baseX = tipX - WHEREIS_HEAD_LEN * ux;
  const baseY = tipY - WHEREIS_HEAD_LEN * uy;
  // Perpendicular to the bearing, for the two rear corners of the head.
  const px = -uy;
  const py = ux;
  const corner = (sign: number) =>
    `${r2(baseX + sign * WHEREIS_HEAD_HALF * px)},${r2(baseY + sign * WHEREIS_HEAD_HALF * py)}`;
  return {
    line: {
      x1: r2(WHEREIS_CENTER + WHEREIS_ARROW_START * ux),
      y1: r2(WHEREIS_CENTER + WHEREIS_ARROW_START * uy),
      x2: r2(baseX),
      y2: r2(baseY),
    },
    head: `${r2(tipX)},${r2(tipY)} ${corner(1)} ${corner(-1)}`,
  };
}

/** Keep in sync with `.daily-whereis-label`: max-width, and name + distance line. */
const WHEREIS_LABEL_MAX_W = 25;
const WHEREIS_LABEL_H = 7.5;
/** 13px glyph in a 440px box, averaged over the Czech alphabet. */
const WHEREIS_CHAR_W = 1.6;

interface WhereIsBox {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

/** Where a label lands, matching how `WhereIsLabel` anchors itself. */
function whereIsLabelBox(arrow: WhereIsArrow, radius: number): WhereIsBox {
  const { ux, uy } = whereIsUnit(arrow.bearing);
  const w = Math.min(WHEREIS_LABEL_MAX_W, WHEREIS_CHAR_W * arrow.name.length);
  const x0 = WHEREIS_CENTER + radius * ux - (w * (1 - ux)) / 2;
  const y0 = WHEREIS_CENTER + radius * uy - (WHEREIS_LABEL_H * (1 - uy)) / 2;
  return { x0, x1: x0 + w, y0, y1: y0 + WHEREIS_LABEL_H };
}

const whereIsOverlap = (a: WhereIsBox, b: WhereIsBox) =>
  Math.max(0, Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0)) *
  Math.max(0, Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0));

/**
 * Label radius of every arrow. Towns that sit in almost the same direction
 * would drop their labels on top of each other, so such a group is fanned out
 * over shorter and shorter arrows instead.
 */
function whereIsRadii(arrows: WhereIsArrow[]): number[] {
  const n = arrows.length;
  const radii = arrows.map(() => WHEREIS_LABEL_RADIUS);
  if (n < 2) return radii;

  const order = arrows.map((_, i) => i).sort((a, b) => arrows[a].bearing - arrows[b].bearing);
  const gapAfter = (k: number) =>
    (arrows[order[(k + 1) % n]].bearing - arrows[order[k]].bearing + 360) % 360;
  // Sweeping from the widest gap keeps a group of neighbours in one piece.
  let widest = 0;
  for (let k = 1; k < n; k++) if (gapAfter(k) > gapAfter(widest)) widest = k;

  let group: number[] = [];
  for (let s = 0; s < n; s++) {
    const k = (widest + 1 + s) % n;
    group.push(order[k]);
    const clashes =
      whereIsOverlap(
        whereIsLabelBox(arrows[order[k]], WHEREIS_LABEL_RADIUS),
        whereIsLabelBox(arrows[order[(k + 1) % n]], WHEREIS_LABEL_RADIUS),
      ) > 0;
    if (!clashes || s === n - 1) {
      whereIsFanOut(arrows, group, radii);
      group = [];
    }
  }
  return radii;
}

/** Spread one group of neighbouring arrows over the available lengths. */
function whereIsFanOut(arrows: WhereIsArrow[], group: number[], radii: number[]): void {
  if (group.length < 2) return;
  const step = (WHEREIS_LABEL_RADIUS - WHEREIS_LABEL_RADIUS_MIN) / (group.length - 1);
  // Only the two monotonic ramps read as a fan; the rest would look scrambled.
  const ramp = group.map((_, i) => WHEREIS_LABEL_RADIUS - i * step);
  let best = ramp;
  let bestScore = Infinity;
  for (const candidate of [ramp, [...ramp].reverse()]) {
    let score = 0;
    for (let i = 0; i < group.length; i++) {
      for (let j = i + 1; j < group.length; j++) {
        score += whereIsOverlap(
          whereIsLabelBox(arrows[group[i]], candidate[i]),
          whereIsLabelBox(arrows[group[j]], candidate[j]),
        );
        // Nudge towards the farther town keeping the longer arrow, so the
        // lengths don't contradict the distances once those are revealed.
        const sameOrder =
          candidate[i] > candidate[j] ===
          (arrows[group[i]].distanceKm > arrows[group[j]].distanceKm);
        if (!sameOrder) score += 0.2;
      }
    }
    if (score < bestScore) {
      bestScore = score;
      best = candidate;
    }
  }
  group.forEach((idx, i) => {
    radii[idx] = best[i];
  });
}

interface WhereIsCardProps {
  entry: DailyWhereIs;
  dayProgress: DailyDayProgress;
  solved: boolean;
  persistDay: (patch: Partial<DailyDayProgress>) => void;
  resetKey: string;
}

function WhereIsCard({ entry, dayProgress, solved, persistDay, resetKey }: WhereIsCardProps) {
  const [input, setInput] = useState("");
  const [bad, setBad] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const givenUp = dayProgress.givenUp;
  const finished = solved || givenUp;
  const hints = dayProgress.whereIs ?? { distances: false, region: false };
  const arrows = useMemo(() => whereIsArrows(entry), [entry]);
  const radii = useMemo(() => whereIsRadii(arrows), [arrows]);

  // Once the day is over there's nothing left to spoil, so show everything.
  const showDistances = finished || hints.distances;
  const showRegion = finished || hints.region;

  useEffect(() => {
    setInput("");
    setBad(false);
    inputRef.current?.focus();
  }, [resetKey, finished]);

  const submit = useCallback(
    (value: string) => {
      if (finished) return;
      const key = normalize(value);
      if (!key) return;
      if (normalize(entry.answer.name) === key) {
        persistDay({ solved: true });
        celebrateDailyWin();
        setInput("");
        return;
      }
      setBad(true);
      setTimeout(() => setBad(false), 380);
    },
    [entry, finished, persistDay],
  );

  const revealHint = useCallback(
    (hint: keyof DailyWhereIsProgress) => {
      if (finished || hints[hint]) return;
      persistDay({ whereIs: { ...hints, [hint]: true } });
    },
    [finished, hints, persistDay],
  );

  const giveUp = useCallback(() => {
    if (finished) return;
    if (!window.confirm(GIVE_UP_CONFIRM)) return;
    persistDay({ givenUp: true });
    setInput("");
  }, [finished, persistDay]);

  const diagramLabel =
    "Šipky od hledaného města: " +
    arrows.map((a) => `${a.name} ${compassLabelCs(a.bearing)}`).join(", ");

  return (
    <div className="daily-whereis">
      <p className="daily-whereis-lead">
        Šipky míří od hledaného města k jeho okolí. Které město je uprostřed?
      </p>

      <div className="daily-whereis-diagram" role="img" aria-label={diagramLabel}>
        <svg className="daily-whereis-svg" viewBox="0 0 100 100" aria-hidden="true">
          {arrows.map((arrow, i) => (
            <WhereIsArrowShape key={arrow.name} bearing={arrow.bearing} radius={radii[i]} />
          ))}
        </svg>
        <div className="daily-whereis-center">?</div>
        {arrows.map((arrow, i) => (
          <WhereIsLabel
            key={arrow.name}
            arrow={arrow}
            radius={radii[i]}
            showDistance={showDistances}
          />
        ))}
      </div>

      {finished ? (
        <div className={"daily-result" + (givenUp ? " daily-result-lost" : "")}>
          {givenUp && <div className="daily-result-tag">Vzdáno</div>}
          <div className="daily-result-name">{entry.answer.name}</div>
          <p className="daily-result-desc">{entry.answer.region}</p>
        </div>
      ) : (
        <>
          <form
            className="daily-input-row"
            onSubmit={(e) => {
              e.preventDefault();
              submit(input);
            }}
          >
            <input
              ref={inputRef}
              className={"input" + (bad ? " input-bad" : "")}
              placeholder="Napiš název města…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="words"
              spellCheck={false}
              aria-label="Odpověď"
            />
          </form>

          {showRegion && (
            <ul className="daily-hints">
              <li>Kraj: {entry.answer.region}</li>
            </ul>
          )}

          <div className="daily-actions">
            {!showDistances && (
              <button
                type="button"
                className="btn daily-hint-btn"
                onClick={() => revealHint("distances")}
              >
                Zobrazit vzdálenosti
              </button>
            )}
            {!showRegion && (
              <button
                type="button"
                className="btn daily-hint-btn"
                onClick={() => revealHint("region")}
              >
                Zobrazit kraj
              </button>
            )}
            <button type="button" className="btn btn-danger daily-give-up-btn" onClick={giveUp}>
              Vzdát se
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function WhereIsArrowShape({ bearing, radius }: { bearing: number; radius: number }) {
  const { line, head } = whereIsGeometry(bearing, radius);
  return (
    <g className="daily-whereis-arrow">
      <line x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} />
      <polygon points={head} />
    </g>
  );
}

function WhereIsLabel({
  arrow,
  radius,
  showDistance,
}: {
  arrow: WhereIsArrow;
  radius: number;
  showDistance: boolean;
}) {
  const { ux, uy } = whereIsUnit(arrow.bearing);
  return (
    <div
      className="daily-whereis-label"
      style={{
        left: `${r2(WHEREIS_CENTER + radius * ux)}%`,
        top: `${r2(WHEREIS_CENTER + radius * uy)}%`,
        // Anchors the label's trailing edge at that point, so it always
        // reads outwards from the centre instead of over the arrow.
        transform: `translate(${r2(ux * 50 - 50)}%, ${r2(uy * 50 - 50)}%)`,
        textAlign: ux > 0.35 ? "left" : ux < -0.35 ? "right" : "center",
      }}
    >
      <span className="daily-whereis-label-name">{arrow.name}</span>
      {showDistance && (
        <span className="daily-whereis-label-dist">{formatKm(arrow.distanceKm)}</span>
      )}
    </div>
  );
}

interface PinpointCardProps {
  entry: DailyPinpoint;
  dayProgress: DailyDayProgress;
  solved: boolean;
  persistDay: (patch: Partial<DailyDayProgress>) => void;
  resetKey: string;
}

interface PinpointAttempt {
  point: LatLng;
  distanceKm: number;
  /** Direction from the dropped pin towards the target. */
  bearing: number;
  hit: boolean;
}

function PinpointCard({
  entry,
  dayProgress,
  solved,
  persistDay,
  resetKey,
}: PinpointCardProps) {
  const givenUp = dayProgress.givenUp;
  const finished = solved || givenUp;
  const progress = dayProgress.pinpoint ?? emptyPinpointProgress();
  // Open on the tier the player still has ahead of them.
  const [tier, setTier] = useState<PinpointDifficulty>(() =>
    progress.easy.solved && !progress.harder.solved ? "harder" : "easy",
  );
  const [pending, setPending] = useState<LatLng | null>(null);

  const tierProgress = progress[tier];
  const tierSolved = tierProgress.solved;
  // Once the day is over both answers are on the table anyway.
  const tierOver = finished || tierSolved;
  const target = useMemo(() => pinpointTarget(entry, tier), [entry, tier]);

  useEffect(() => {
    setPending(null);
  }, [tier, resetKey, finished]);

  const attempts = useMemo<PinpointAttempt[]>(() => {
    if (!target) return [];
    return tierProgress.guesses.map((g) => {
      const point: LatLng = { lat: g.lat, lng: g.lng };
      const distanceKm = haversineKm(point, target);
      return {
        point,
        distanceKm,
        bearing: initialBearingDeg(point, target),
        hit: distanceKm <= PINPOINT_HIT_KM,
      };
    });
  }, [tierProgress.guesses, target]);

  const misses = useMemo(
    () => attempts.filter((a) => !a.hit).map((a) => a.point),
    [attempts],
  );
  const hit = useMemo(() => attempts.find((a) => a.hit)?.point ?? null, [attempts]);
  const reveal = useMemo(
    () =>
      tierOver && target
        ? { point: target, label: entry[tier].question, radiusKm: PINPOINT_HIT_KM }
        : null,
    [tierOver, target, entry, tier],
  );

  const confirmPin = useCallback(() => {
    if (!pending || !target || tierOver) return;
    const isHit = haversineKm(pending, target) <= PINPOINT_HIT_KM;
    const next = {
      ...progress,
      [tier]: {
        solved: isHit,
        guesses: [...tierProgress.guesses, { lat: pending.lat, lng: pending.lng }],
      },
    };
    const allSolved = PINPOINT_DIFFICULTIES.every((d) => next[d].solved);
    persistDay({ pinpoint: next, ...(allSolved ? { solved: true } : {}) });
    if (allSolved) celebrateDailyWin();
    setPending(null);
  }, [pending, target, tierOver, progress, tier, tierProgress.guesses, persistDay]);

  const revealOutline = useCallback(() => {
    if (progress.outline) return;
    persistDay({ pinpoint: { ...progress, outline: true } });
  }, [progress, persistDay]);

  const giveUp = useCallback(() => {
    if (finished) return;
    if (!window.confirm(GIVE_UP_CONFIRM)) return;
    persistDay({ givenUp: true });
    setPending(null);
  }, [finished, persistDay]);

  const otherTier: PinpointDifficulty = tier === "easy" ? "harder" : "easy";
  const lastMiss = tierSolved ? null : (attempts[attempts.length - 1] ?? null);

  return (
    <div className="daily-pinpoint">
      <div className="daily-pinpoint-tiers" role="tablist" aria-label="Obtížnost">
        {PINPOINT_DIFFICULTIES.map((d) => (
          <button
            key={d}
            type="button"
            role="tab"
            aria-selected={tier === d}
            className={
              "daily-pinpoint-tier" +
              (tier === d ? " active" : "") +
              (progress[d].solved ? " is-solved" : "")
            }
            onClick={() => setTier(d)}
          >
            {PINPOINT_TIER_LABEL[d]}
            {progress[d].solved && <span aria-hidden="true"> ✓</span>}
          </button>
        ))}
      </div>

      <div className="daily-pinpoint-question">
        <span className="daily-pinpoint-question-label">Najdi na mapě</span>
        <span className="daily-pinpoint-question-name">{entry[tier].question}</span>
      </div>

      <PinpointMap
        pending={pending}
        onPick={setPending}
        misses={misses}
        hit={hit}
        reveal={reveal}
        outline={progress.outline}
        locked={tierOver}
        frameKey={`${resetKey}:${tier}`}
      />

      {tierOver ? (
        <div className={"daily-result" + (tierSolved ? "" : " daily-result-lost")}>
          {!tierSolved && <div className="daily-result-tag">Netrefeno</div>}
          <div className="daily-result-name">{entry[tier].question}</div>
          <p className="daily-result-desc">
            {tierSolved
              ? `Trefa na ${attempts.length}. pokus – ${formatDistance(
                  attempts[attempts.length - 1]?.distanceKm ?? 0,
                )} od cíle.`
              : attempts.length > 0
                ? `Nejblíž jsi byl ${formatDistance(
                    Math.min(...attempts.map((a) => a.distanceKm)),
                  )} od cíle.`
                : "Tenhle tip jsi nezkusil."}
          </p>
          {!progress[otherTier].solved && !finished && (
            <button
              type="button"
              className="btn daily-pinpoint-next"
              onClick={() => setTier(otherTier)}
            >
              Pokračovat na {PINPOINT_TIER_LABEL[otherTier].toLowerCase()} zadání
            </button>
          )}
        </div>
      ) : (
        <>
          {lastMiss && (
            <div className="daily-pinpoint-feedback">
              Minul jsi o {formatDistance(lastMiss.distanceKm)} – cíl leží{" "}
              {compassLabelCs(lastMiss.bearing)} od tvého špendlíku.
            </div>
          )}

          {attempts.length > 1 && (
            <ol className="daily-pinpoint-attempts">
              {attempts.map((a, i) => (
                <li key={i}>
                  <span className="daily-pinpoint-attempt-no">{i + 1}.</span>
                  {formatDistance(a.distanceKm)} · {compassLabelCs(a.bearing)}
                </li>
              ))}
            </ol>
          )}

          <div className="daily-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={confirmPin}
              disabled={!pending}
            >
              {pending ? "Potvrdit špendlík" : "Klikni do mapy"}
            </button>
            {!progress.outline && (
              <button type="button" className="btn daily-hint-btn" onClick={revealOutline}>
                Zobrazit obrys Česka
              </button>
            )}
            <button type="button" className="btn btn-danger daily-give-up-btn" onClick={giveUp}>
              Vzdát se
            </button>
          </div>
        </>
      )}
    </div>
  );
}

interface OddOneOutCardProps {
  entry: DailyOddOneOut;
  dayProgress: DailyDayProgress;
  solved: boolean;
  persistDay: (patch: Partial<DailyDayProgress>) => void;
  /** Keeps the shuffled order stable for a given day across reloads. */
  shuffleSeed: string;
}

function OddOneOutCard({
  entry,
  dayProgress,
  solved,
  persistDay,
  shuffleSeed,
}: OddOneOutCardProps) {
  const givenUp = dayProgress.givenUp;
  const finished = solved || givenUp;
  const progress = dayProgress.oddOneOut ?? emptyOddOneOutProgress();
  const answer = oddOneOutAnswer(entry);
  const options = useMemo(
    () => shuffleOddOneOut(entry, shuffleSeed),
    [entry, shuffleSeed],
  );
  const picked = useMemo(() => new Set(progress.picks), [progress.picks]);

  const pick = useCallback(
    (option: string) => {
      if (finished || picked.has(option)) return;
      const picks = [...progress.picks, option];
      const isAnswer = option === answer;
      persistDay({ oddOneOut: { picks }, ...(isAnswer ? { solved: true } : {}) });
      if (isAnswer) celebrateDailyWin();
    },
    [finished, picked, progress.picks, answer, persistDay],
  );

  const giveUp = useCallback(() => {
    if (finished) return;
    if (!window.confirm(GIVE_UP_CONFIRM)) return;
    persistDay({ givenUp: true });
  }, [finished, persistDay]);

  return (
    <div className="daily-odd">
      <p className="daily-odd-lead">
        Tři z těchto čtyř položek spojuje jedna vlastnost. Která do skupiny nepatří?
      </p>

      <div className="daily-odd-options" role="group" aria-label="Možnosti">
        {options.map((option) => {
          const isAnswer = option === answer;
          const wasPicked = picked.has(option);
          const showAsAnswer = finished && isAnswer;
          const showAsWrong = wasPicked && !isAnswer;
          return (
            <button
              key={option}
              type="button"
              className={
                "daily-odd-option" +
                (showAsAnswer ? " is-correct" : "") +
                (showAsWrong ? " is-wrong" : "")
              }
              onClick={() => pick(option)}
              disabled={finished || wasPicked}
            >
              <span className="daily-odd-option-name">{option}</span>
              {(showAsAnswer || showAsWrong) && (
                <span className="daily-odd-option-mark" aria-hidden="true">
                  {showAsAnswer ? "✓" : "✗"}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {finished ? (
        <div className={"daily-result" + (solved ? "" : " daily-result-lost")}>
          {givenUp && <div className="daily-result-tag">Vzdáno</div>}
          <div className="daily-result-name">{answer}</div>
          <p className="daily-result-desc">{entry.afterword}</p>
        </div>
      ) : (
        <div className="daily-actions">
          <button type="button" className="btn btn-danger daily-give-up-btn" onClick={giveUp}>
            Vzdát se
          </button>
        </div>
      )}
    </div>
  );
}

interface HigherLowerCardProps {
  entry: DailyHigherLower;
  dayProgress: DailyDayProgress;
  solved: boolean;
  persistDay: (patch: Partial<DailyDayProgress>) => void;
}

function HigherLowerCard({ entry, dayProgress, solved, persistDay }: HigherLowerCardProps) {
  const progress = dayProgress.higherLower ?? emptyHigherLowerProgress();
  const picks = progress.picks;
  const rounds = higherLowerRounds(entry);
  const score = higherLowerScore(entry, picks);
  const givenUp = dayProgress.givenUp;
  const finished = solved || givenUp || picks.length >= rounds;

  /**
   * Set right after a pick: the window stays put with both values on show
   * until the player asks for the next pair. Deliberately not persisted –
   * a reload just moves on, the verdict is in the track either way.
   */
  const [reviewing, setReviewing] = useState(false);
  const round = reviewing ? picks.length - 1 : picks.length;

  const pick = useCallback(
    (index: number) => {
      if (finished || reviewing) return;
      if (index !== round && index !== round + 1) return;
      const nextPicks = [...picks, index];
      const done = nextPicks.length >= rounds;
      persistDay({
        higherLower: { picks: nextPicks },
        ...(done ? { solved: true } : {}),
      });
      if (done) {
        if (higherLowerScore(entry, nextPicks) === rounds) celebrateDailyWin();
      } else {
        setReviewing(true);
      }
    },
    [entry, finished, reviewing, round, picks, rounds, persistDay],
  );

  const giveUp = useCallback(() => {
    if (finished) return;
    if (!window.confirm(GIVE_UP_CONFIRM)) return;
    persistDay({ givenUp: true });
  }, [finished, persistDay]);

  const left = entry.options[round];
  const right = entry.options[round + 1];
  const lastPick = reviewing ? picks[picks.length - 1] : undefined;
  const lastWin = lastPick !== undefined && higherLowerPickWins(entry, round, lastPick);
  const higherName = higherLowerPickWins(entry, round, round) ? left?.[0] : right?.[0];

  const optionState = (index: number): string => {
    if (!reviewing) return "";
    if (higherLowerPickWins(entry, round, index)) return " is-correct";
    return index === lastPick ? " is-wrong" : "";
  };

  return (
    <div className="daily-hl">
      <p className="daily-hl-spec">{entry.specification}</p>

      {finished ? (
        <HigherLowerResult
          entry={entry}
          picks={picks}
          score={score}
          rounds={rounds}
          givenUp={givenUp}
        />
      ) : (
        <>
          <div className="daily-hl-meta">
            <span>
              Kolo {round + 1} / {rounds}
            </span>
            <span className="daily-hl-score">Skóre {score}</span>
          </div>

          <div className="daily-hl-duel" role="group" aria-label="Dvojice k porovnání">
            {left && (
              <HigherLowerOption
                option={left}
                revealed
                stateClass={optionState(round)}
                disabled={reviewing}
                onPick={() => pick(round)}
              />
            )}
            <div className="daily-hl-vs" aria-hidden="true">
              vs
            </div>
            {right && (
              <HigherLowerOption
                option={right}
                revealed={reviewing}
                stateClass={optionState(round + 1)}
                disabled={reviewing}
                onPick={() => pick(round + 1)}
              />
            )}
          </div>

          <HigherLowerTrack
            entry={entry}
            picks={picks}
            rounds={rounds}
            currentRound={round}
          />

          {reviewing && (
            <div className={"daily-hl-verdict" + (lastWin ? " is-win" : "")}>
              {lastWin ? "Správně" : "Vedle"} – vyšší hodnotu má {higherName}.
            </div>
          )}

          <div className="daily-actions">
            {reviewing && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setReviewing(false)}
              >
                Další dvojice
              </button>
            )}
            <button type="button" className="btn btn-danger daily-give-up-btn" onClick={giveUp}>
              Vzdát se
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function HigherLowerOption({
  option,
  revealed,
  stateClass,
  disabled,
  onPick,
}: {
  option: DailyHigherLowerOption;
  revealed: boolean;
  stateClass: string;
  disabled: boolean;
  onPick: () => void;
}) {
  const [name, value] = option;
  return (
    <button
      type="button"
      className={"daily-hl-option" + stateClass}
      onClick={onPick}
      disabled={disabled}
      aria-label={`Vybrat ${name} jako vyšší`}
    >
      <span className="daily-hl-option-name">{name}</span>
      <span className={"daily-hl-option-value" + (revealed ? "" : " is-hidden")}>
        {revealed ? formatHigherLowerValue(value) : "?"}
      </span>
    </button>
  );
}

function HigherLowerTrack({
  entry,
  picks,
  rounds,
  currentRound,
}: {
  entry: DailyHigherLower;
  picks: number[];
  rounds: number;
  currentRound: number;
}) {
  return (
    <ol className="daily-hl-track" aria-label="Průběh kol">
      {Array.from({ length: rounds }, (_, i) => {
        const played = i < picks.length;
        const win = played && higherLowerPickWins(entry, i, picks[i]!);
        return (
          <li
            key={i}
            className={
              "daily-hl-track-step" +
              (played ? (win ? " is-win" : " is-loss") : "") +
              (i === currentRound ? " is-current" : "")
            }
          >
            {played ? (win ? "✓" : "✗") : i + 1}
          </li>
        );
      })}
    </ol>
  );
}

function HigherLowerResult({
  entry,
  picks,
  score,
  rounds,
  givenUp,
}: {
  entry: DailyHigherLower;
  picks: number[];
  score: number;
  rounds: number;
  givenUp: boolean;
}) {
  return (
    <div className={"daily-result" + (score === rounds ? "" : " daily-result-lost")}>
      {givenUp && <div className="daily-result-tag">Vzdáno</div>}
      <div className="daily-result-name">
        {score} / {rounds} bodů
      </div>
      <p className="daily-result-desc">{entry.specification}</p>
      <ol className="daily-hl-summary">
        {entry.options.map(([name, value], i) => {
          const played = i > 0 && i - 1 < picks.length;
          const win = played && higherLowerPickWins(entry, i - 1, picks[i - 1]!);
          return (
            <li key={`${name}-${i}`}>
              <span
                className={
                  "daily-hl-summary-mark" +
                  (played ? (win ? " is-win" : " is-loss") : "")
                }
                aria-hidden="true"
              >
                {i === 0 ? "" : played ? (win ? "✓" : "✗") : "–"}
              </span>
              <span className="daily-hl-summary-name">{name}</span>
              <span className="daily-hl-summary-value">{formatHigherLowerValue(value)}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/* ── Organize (arrange the items by the given criterion) ───────────── */

/** Gap between two tiles, in sync with `.daily-organize-list`. */
const ORGANIZE_GAP = 10;
/** Index badge plus the tile's own padding – everything but the name. */
const ORGANIZE_TILE_CHROME = 34;
/** 15px semibold glyph, averaged over the Czech alphabet. */
const ORGANIZE_CHAR_W = 8.2;
const ORGANIZE_TILE_MIN = 92;

/**
 * Width one row of tiles needs. Names wrap inside a tile, so only their
 * longest word has to fit; anything narrower than that would break words
 * mid-syllable and read badly.
 */
function organizeRowWidth(names: string[]): number {
  const tile = (name: string) => {
    const longest = Math.max(...name.split(/\s+/).map((word) => word.length), 0);
    return Math.max(ORGANIZE_TILE_MIN, ORGANIZE_CHAR_W * longest + ORGANIZE_TILE_CHROME);
  };
  const gaps = Math.max(0, names.length - 1) * ORGANIZE_GAP;
  return names.reduce((sum, name) => sum + tile(name), gaps);
}

/**
 * Whether the board has to stack into a single column. The criterion names
 * its own two ends („od západu na východ“), so either axis reads fine – but
 * a row squeezed past `organizeRowWidth` doesn't.
 */
function useOrganizeVertical(ref: RefObject<HTMLElement | null>, names: string[]): boolean {
  const needed = useMemo(() => organizeRowWidth(names), [names]);
  const [vertical, setVertical] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // The list is a plain block, so its width doesn't depend on the answer
    // we're about to give – no risk of the observer flip-flopping.
    const measure = () => setVertical(el.clientWidth > 0 && el.clientWidth < needed);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, needed]);

  return vertical;
}

interface OrganizeDrag {
  pointerId: number;
  /** Index the dragged tile started at. */
  from: number;
  /** Index it would land on if the player let go now. */
  to: number;
  /** Where the pointer went down, along the layout axis, relative to the list. */
  start: number;
  /** How far to offset the tile from the slot it currently renders in. */
  shift: number;
  /** Centre of every slot along the axis, as measured when the drag began. */
  centers: number[];
}

interface OrganizeCardProps {
  entry: DailyOrganize;
  dayProgress: DailyDayProgress;
  solved: boolean;
  persistDay: (patch: Partial<DailyDayProgress>) => void;
  /** Keeps the starting order stable for a given day across reloads. */
  shuffleSeed: string;
}

function OrganizeCard({
  entry,
  dayProgress,
  solved,
  persistDay,
  shuffleSeed,
}: OrganizeCardProps) {
  const givenUp = dayProgress.givenUp;
  const finished = solved || givenUp;
  const progress = dayProgress.organize ?? emptyOrganizeProgress();
  const solution = useMemo(() => organizeSolution(entry), [entry]);
  const start = useMemo(() => shuffleOrganize(entry, shuffleSeed), [entry, shuffleSeed]);
  // An arrangement stored against a different set of items is worthless.
  const saved = organizeOrderFits(entry, progress.order) ? progress.order : start;
  // Giving up puts the solution on the board without overwriting the attempt.
  const order = givenUp ? solution : saved;

  const listRef = useRef<HTMLOListElement>(null);
  const vertical = useOrganizeVertical(listRef, solution);
  const [drag, setDrag] = useState<OrganizeDrag | null>(null);
  /** Verdict of the last check – transient, it's stale the moment they rearrange. */
  const [wrong, setWrong] = useState(false);

  const rearrange = useCallback(
    (next: string[]) => {
      persistDay({ organize: { ...progress, order: next } });
      setWrong(false);
    },
    [progress, persistDay],
  );

  const nudge = useCallback(
    (index: number, step: number) => {
      const to = index + step;
      if (finished || to < 0 || to >= order.length) return;
      rearrange(organizeMove(order, index, to));
    },
    [finished, order, rearrange],
  );

  /** Pointer position along the layout axis, relative to the list box. */
  const axisPos = useCallback(
    (event: ReactPointerEvent): number | null => {
      const list = listRef.current;
      if (!list) return null;
      const box = list.getBoundingClientRect();
      return vertical ? event.clientY - box.top : event.clientX - box.left;
    },
    [vertical],
  );

  const onPointerDown = useCallback(
    (index: number, event: ReactPointerEvent<HTMLLIElement>) => {
      if (finished || drag) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      // The nudge buttons are inside the tile and have their own job.
      if ((event.target as HTMLElement).closest("button")) return;
      const list = listRef.current;
      const pos = axisPos(event);
      if (!list || pos === null) return;
      const box = list.getBoundingClientRect();
      const centers = Array.from(list.children, (child) => {
        const r = child.getBoundingClientRect();
        return vertical ? r.top + r.height / 2 - box.top : r.left + r.width / 2 - box.left;
      });
      // Keeps the tile following the pointer even once it leaves the list.
      event.currentTarget.setPointerCapture(event.pointerId);
      // Stops the browser from starting a text selection instead.
      event.preventDefault();
      setDrag({ pointerId: event.pointerId, from: index, to: index, start: pos, shift: 0, centers });
    },
    [finished, drag, vertical, axisPos],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLLIElement>) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      const pos = axisPos(event);
      if (pos === null) return;
      const wanted = drag.centers[drag.from]! + (pos - drag.start);
      let to = drag.from;
      for (let i = 0; i < drag.centers.length; i++) {
        if (Math.abs(drag.centers[i]! - wanted) < Math.abs(drag.centers[to]! - wanted)) to = i;
      }
      setDrag({ ...drag, to, shift: wanted - drag.centers[to]! });
    },
    [drag, axisPos],
  );

  const endDrag = useCallback(
    (event: ReactPointerEvent<HTMLLIElement>) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      if (drag.to !== drag.from) rearrange(organizeMove(order, drag.from, drag.to));
      setDrag(null);
    },
    [drag, order, rearrange],
  );

  const onKeyDown = useCallback(
    (index: number, event: ReactKeyboardEvent<HTMLLIElement>) => {
      const back = vertical ? "ArrowUp" : "ArrowLeft";
      const forth = vertical ? "ArrowDown" : "ArrowRight";
      if (event.key !== back && event.key !== forth) return;
      event.preventDefault();
      nudge(index, event.key === back ? -1 : 1);
    },
    [vertical, nudge],
  );

  const check = useCallback(() => {
    if (finished) return;
    const right = organizeIsSolved(entry, order);
    persistDay({
      organize: { order, attempts: progress.attempts + 1 },
      ...(right ? { solved: true } : {}),
    });
    setWrong(!right);
    if (right) celebrateDailyWin();
  }, [finished, entry, order, progress.attempts, persistDay]);

  const giveUp = useCallback(() => {
    if (finished) return;
    if (!window.confirm(GIVE_UP_CONFIRM)) return;
    persistDay({ givenUp: true });
    setDrag(null);
    setWrong(false);
  }, [finished, persistDay]);

  // While dragging, the board shows where the tile would land.
  const shown = drag ? organizeMove(order, drag.from, drag.to) : order;
  const draggedName = drag ? order[drag.from] : null;

  return (
    <div className="daily-organize">
      <p className="daily-organize-criteria">{entry.criteria}</p>

      {!finished && (
        <p className="daily-organize-lead">
          Přetáhni položky na správná místa – nebo je posouvej tlačítky{" "}
          {vertical ? "▲ ▼" : "◀ ▶"}.
        </p>
      )}

      <ol
        ref={listRef}
        className={"daily-organize-list" + (vertical ? " is-vertical" : "")}
        aria-label="Pořadí položek"
      >
        {shown.map((name, i) => {
          const dragged = name === draggedName;
          const label = organizeLabel(entry, name);
          return (
            <li
              key={name}
              className={
                "daily-organize-item" +
                (dragged ? " is-dragging" : "") +
                (solved ? " is-correct" : givenUp ? " is-revealed" : "")
              }
              style={
                dragged && drag
                  ? {
                      transform: vertical
                        ? `translateY(${drag.shift}px)`
                        : `translateX(${drag.shift}px)`,
                    }
                  : undefined
              }
              tabIndex={finished ? undefined : 0}
              aria-label={`${i + 1}. ${name}`}
              onPointerDown={finished ? undefined : (e) => onPointerDown(i, e)}
              onPointerMove={finished ? undefined : onPointerMove}
              onPointerUp={finished ? undefined : endDrag}
              onLostPointerCapture={finished ? undefined : endDrag}
              onKeyDown={finished ? undefined : (e) => onKeyDown(i, e)}
            >
              <span className="daily-organize-item-pos" aria-hidden="true">
                {i + 1}
              </span>
              <span className="daily-organize-item-name">{name}</span>

              {finished
                ? label && <span className="daily-organize-item-label">{label}</span>
                : (
                  <span className="daily-organize-item-nudges">
                    <button
                      type="button"
                      className="daily-organize-nudge"
                      onClick={() => nudge(i, -1)}
                      disabled={i === 0}
                      aria-label={`Posunout ${name} o místo ${vertical ? "výš" : "vlevo"}`}
                    >
                      {vertical ? "▲" : "◀"}
                    </button>
                    <button
                      type="button"
                      className="daily-organize-nudge"
                      onClick={() => nudge(i, 1)}
                      disabled={i === shown.length - 1}
                      aria-label={`Posunout ${name} o místo ${vertical ? "níž" : "vpravo"}`}
                    >
                      {vertical ? "▼" : "▶"}
                    </button>
                  </span>
                )}
            </li>
          );
        })}
      </ol>

      {finished ? (
        <div className={"daily-result" + (solved ? "" : " daily-result-lost")}>
          {givenUp && <div className="daily-result-tag">Vzdáno</div>}
          <div className="daily-result-name">
            {solved ? `Správně na ${progress.attempts}. pokus` : "Správné pořadí"}
          </div>
          <p className="daily-result-desc">{entry.criteria}</p>
        </div>
      ) : (
        <>
          {wrong && (
            <div className="daily-organize-verdict">
              Tohle pořadí správné není. Zkus to znovu.
            </div>
          )}

          <div className="daily-actions">
            <button type="button" className="btn btn-primary" onClick={check}>
              Zkontrolovat
            </button>
            {progress.attempts > 0 && (
              <span className="daily-organize-attempts">Pokusů: {progress.attempts}</span>
            )}
            <button type="button" className="btn btn-danger daily-give-up-btn" onClick={giveUp}>
              Vzdát se
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* ── Trip (fill in the missing stops on a route) ──────────────────── */

/** Connector width, in sync with `.daily-trip-edge`. */
const TRIP_CONNECTOR = 40;
/** Padding plus the station dot – everything but the name. */
const TRIP_NODE_CHROME = 16;
/** 14px semibold glyph, averaged over the Czech alphabet. */
const TRIP_CHAR_W = 7.4;
const TRIP_NODE_MIN = 68;
/** Room for a compact text field in a blank stop. */
const TRIP_BLANK_MIN = 80;

/**
 * Width one row of stops needs. Names wrap inside a node, so only their
 * longest word has to fit; blanks need enough for a usable input.
 */
function tripRowWidth(entry: DailyTrip): number {
  const nodes = entry.sequence.reduce((sum, stop, i) => {
    if (!tripIsGiven(entry, i)) return sum + TRIP_BLANK_MIN;
    const longest = Math.max(...stop.place.split(/\s+/).map((word) => word.length), 0);
    return sum + Math.max(TRIP_NODE_MIN, TRIP_CHAR_W * longest + TRIP_NODE_CHROME);
  }, 0);
  const edges = Math.max(0, entry.sequence.length - 1) * TRIP_CONNECTOR;
  return nodes + edges;
}

/**
 * Whether the route has to stack into a single column. A row is the
 * natural reading of a trip, but squeezed nodes make the blanks unusable.
 */
function useTripVertical(ref: RefObject<HTMLElement | null>, entry: DailyTrip): boolean {
  const needed = useMemo(() => tripRowWidth(entry), [entry]);
  const [vertical, setVertical] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setVertical(el.clientWidth > 0 && el.clientWidth < needed);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, needed]);

  return vertical;
}

interface TripCardProps {
  entry: DailyTrip;
  dayProgress: DailyDayProgress;
  solved: boolean;
  persistDay: (patch: Partial<DailyDayProgress>) => void;
  resetKey: string;
}

function TripCard({ entry, dayProgress, solved, persistDay, resetKey }: TripCardProps) {
  const givenUp = dayProgress.givenUp;
  const finished = solved || givenUp;
  const progress = dayProgress.trip ?? emptyTripProgress();
  const blanks = useMemo(() => tripBlankIndices(entry), [entry]);
  const solvedSet = useMemo(() => new Set(progress.solved), [progress.solved]);
  const lastStop = entry.sequence[entry.sequence.length - 1];

  const diagramRef = useRef<HTMLDivElement>(null);
  const vertical = useTripVertical(diagramRef, entry);
  const inputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const focusNextBlank = useCallback(() => {
    const next = blanks.find((i) => !solvedSet.has(i));
    if (next !== undefined) inputRefs.current[next]?.focus();
  }, [blanks, solvedSet]);

  useEffect(() => {
    if (finished) return;
    focusNextBlank();
  }, [resetKey, finished, focusNextBlank]);

  const answerStop = useCallback(
    (index: number, value: string) => {
      if (finished || tripIsGiven(entry, index) || solvedSet.has(index)) return false;
      const stop = entry.sequence[index];
      if (!stop) return false;
      const key = normalize(value);
      if (!key) return false;
      if (normalize(stop.place) !== key) return false;

      const nextSolved = [...new Set([...progress.solved, index])];
      const allDone = tripAllSolved(entry, nextSolved);
      persistDay({
        trip: { solved: nextSolved },
        ...(allDone ? { solved: true } : {}),
      });
      if (allDone) celebrateDailyWin();
      return true;
    },
    [entry, finished, solvedSet, progress.solved, persistDay],
  );

  const giveUp = useCallback(() => {
    if (finished) return;
    if (!window.confirm(GIVE_UP_CONFIRM)) return;
    persistDay({ givenUp: true });
  }, [finished, persistDay]);

  const diagramLabel =
    "Trasa: " +
    entry.sequence
      .map((stop, i) =>
        tripIsGiven(entry, i) || solvedSet.has(i) || finished ? stop.place : "chybějící zastávka",
      )
      .join(", ");

  return (
    <div className="daily-trip">
      <p className="daily-trip-prompt">{entry.prompt}</p>

      <div
        ref={diagramRef}
        className={"daily-trip-diagram" + (vertical ? " is-vertical" : "")}
        role="group"
        aria-label={diagramLabel}
      >
        {entry.sequence.map((stop, i) => {
          const given = tripIsGiven(entry, i);
          const filled = given || solvedSet.has(i) || finished;
          const correct = solved || (!given && solvedSet.has(i));
          const revealed = !given && givenUp && !solvedSet.has(i);
          const segmentKm = i > 0 ? tripSegmentKm(entry, i - 1) : null;
          return (
            <Fragment key={`${stop.place}-${i}`}>
              {segmentKm !== null && (
                <div className="daily-trip-edge" aria-hidden="true">
                  <span className="daily-trip-edge-km">{formatKm(segmentKm)}</span>
                  <span className="daily-trip-edge-shaft" />
                </div>
              )}
              <div
                className={
                  "daily-trip-node" +
                  (given ? " is-given" : "") +
                  (correct ? " is-correct" : "") +
                  (revealed ? " is-revealed" : "") +
                  (!filled ? " is-blank" : "")
                }
              >
                <span className="daily-trip-node-dot" aria-hidden="true" />
                {filled ? (
                  <span className="daily-trip-node-name">{stop.place}</span>
                ) : (
                  <TripBlank
                    index={i}
                    onAnswer={answerStop}
                    inputRef={(el) => {
                      inputRefs.current[i] = el;
                    }}
                  />
                )}
              </div>
            </Fragment>
          );
        })}
      </div>

      {finished ? (
        <div className={"daily-result" + (solved ? "" : " daily-result-lost")}>
          {givenUp && <div className="daily-result-tag">Vzdáno</div>}
          <div className="daily-result-name">{solved ? "Správně" : "Správná trasa"}</div>
          <p className="daily-result-desc">
            {tripRouteLabel(entry)}
            {lastStop ? ` · ${formatKm(lastStop.km)}` : ""}
          </p>
        </div>
      ) : (
        <div className="daily-actions">
          <button type="button" className="btn btn-danger daily-give-up-btn" onClick={giveUp}>
            Vzdát se
          </button>
        </div>
      )}
    </div>
  );
}

function TripBlank({
  index,
  onAnswer,
  inputRef,
}: {
  index: number;
  onAnswer: (index: number, value: string) => boolean;
  inputRef: (el: HTMLInputElement | null) => void;
}) {
  const [input, setInput] = useState("");
  const [bad, setBad] = useState(false);

  const submit = () => {
    if (!input.trim()) return;
    const ok = onAnswer(index, input);
    if (ok) {
      setInput("");
    } else {
      setBad(true);
      setTimeout(() => setBad(false), 380);
    }
  };

  return (
    <form
      className="daily-trip-blank"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <input
        ref={inputRef}
        className={"input daily-trip-input" + (bad ? " input-bad" : "")}
        placeholder="?"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="words"
        spellCheck={false}
        aria-label={`Zastávka ${index + 1}`}
      />
    </form>
  );
}

/* ── Sort (drop every item into its category) ─────────────────────── */

/** A drop target: a category index, or the pile of items not yet placed. */
type SortZone = number | "pool";

/** How far the pointer has to travel before a press turns into a drag. */
const SORT_DRAG_THRESHOLD = 6;
/** Band along the scroll container's edges that scrolls it while dragging. */
const SORT_SCROLL_EDGE = 56;
const SORT_SCROLL_MAX_SPEED = 14;

interface SortDrag {
  pointerId: number;
  name: string;
  startX: number;
  startY: number;
  x: number;
  y: number;
  /** Where inside the chip it was grabbed, so the ghost doesn't jump. */
  grabX: number;
  grabY: number;
  width: number;
  /** Past the threshold – until then the press may still be a tap. */
  active: boolean;
  over: SortZone | null;
}

function sortZoneAt(x: number, y: number): SortZone | null {
  const el = document.elementFromPoint(x, y)?.closest<HTMLElement>("[data-sort-zone]");
  const raw = el?.dataset.sortZone;
  if (raw === undefined) return null;
  if (raw === "pool") return "pool";
  const index = Number(raw);
  return Number.isInteger(index) ? index : null;
}

function scrollParent(el: HTMLElement | null): HTMLElement | null {
  for (let node = el?.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) {
      return node;
    }
  }
  return document.scrollingElement as HTMLElement | null;
}

interface SortCardProps {
  entry: DailySort;
  dayProgress: DailyDayProgress;
  solved: boolean;
  persistDay: (patch: Partial<DailyDayProgress>) => void;
  /** Keeps the order of the pile stable for a given day across reloads. */
  shuffleSeed: string;
}

function SortCard({ entry, dayProgress, solved, persistDay, shuffleSeed }: SortCardProps) {
  const givenUp = dayProgress.givenUp;
  const finished = solved || givenUp;
  const progress = dayProgress.sort ?? emptySortProgress();
  const solution = useMemo(() => sortSolution(entry), [entry]);
  const pile = useMemo(() => shuffleSort(entry, shuffleSeed), [entry, shuffleSeed]);
  const saved = useMemo(
    () => sortPlacementFits(entry, progress.placement),
    [entry, progress.placement],
  );
  // Giving up puts the solution in the bins without overwriting the attempt.
  const placement = givenUp ? solution : saved;
  const total = pile.length;
  const pool = pile.filter((name) => !(name in placement));

  const rootRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [drag, setDragState] = useState<SortDrag | null>(null);
  // Pointer handlers and the scroll loop need the latest drag between renders.
  const dragRef = useRef<SortDrag | null>(null);
  const setDrag = useCallback((next: SortDrag | null) => {
    dragRef.current = next;
    setDragState(next);
  }, []);
  /** The click that trails a finished drag must not also select the chip. */
  const suppressClick = useRef(false);
  /** Correct count of the last check – transient, stale the moment they move anything. */
  const [verdict, setVerdict] = useState<number | null>(null);

  const place = useCallback(
    (name: string, zone: SortZone) => {
      if (finished) return;
      const current = saved[name];
      if (zone === "pool" ? current === undefined : current === zone) return;
      const next = { ...saved };
      if (zone === "pool") delete next[name];
      else next[name] = zone;
      persistDay({ sort: { ...progress, placement: next } });
      setVerdict(null);
    },
    [finished, saved, progress, persistDay],
  );

  const onZoneClick = useCallback(
    (zone: SortZone) => {
      if (!selected) return;
      place(selected, zone);
      setSelected(null);
    },
    [selected, place],
  );

  const onPointerDown = useCallback(
    (name: string, event: ReactPointerEvent<HTMLButtonElement>) => {
      suppressClick.current = false;
      if (finished || dragRef.current) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      const box = event.currentTarget.getBoundingClientRect();
      // Keeps the chip following the pointer even once it leaves the chip.
      event.currentTarget.setPointerCapture(event.pointerId);
      setDrag({
        pointerId: event.pointerId,
        name,
        startX: event.clientX,
        startY: event.clientY,
        x: event.clientX,
        y: event.clientY,
        grabX: event.clientX - box.left,
        grabY: event.clientY - box.top,
        width: box.width,
        active: false,
        over: null,
      });
    },
    [finished, setDrag],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      const current = dragRef.current;
      if (!current || event.pointerId !== current.pointerId) return;
      const active =
        current.active ||
        Math.hypot(event.clientX - current.startX, event.clientY - current.startY) >
          SORT_DRAG_THRESHOLD;
      if (active && !current.active) setSelected(null);
      setDrag({
        ...current,
        x: event.clientX,
        y: event.clientY,
        active,
        over: active ? sortZoneAt(event.clientX, event.clientY) : null,
      });
    },
    [setDrag],
  );

  const onPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      const current = dragRef.current;
      if (!current || event.pointerId !== current.pointerId) return;
      if (current.active) {
        suppressClick.current = true;
        if (current.over !== null) place(current.name, current.over);
      }
      setDrag(null);
    },
    [place, setDrag],
  );

  const cancelDrag = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (dragRef.current?.pointerId === event.pointerId) setDrag(null);
    },
    [setDrag],
  );

  const onChipClick = useCallback(
    (name: string, event: ReactMouseEvent<HTMLButtonElement>) => {
      // The bin around the chip would take the click as "drop it here".
      event.stopPropagation();
      if (suppressClick.current) {
        suppressClick.current = false;
        return;
      }
      if (finished) return;
      setSelected((prev) => (prev === name ? null : name));
    },
    [finished],
  );

  const onChipKeyDown = useCallback(
    (name: string, event: ReactKeyboardEvent<HTMLButtonElement>) => {
      if (event.key === "Escape") {
        setSelected(null);
        return;
      }
      if (!/^[0-9]$/.test(event.key)) return;
      const digit = Number(event.key);
      if (digit > entry.categories.length) return;
      event.preventDefault();
      place(name, digit === 0 ? "pool" : digit - 1);
      setSelected(null);
    },
    [entry.categories.length, place],
  );

  const dragActive = Boolean(drag?.active);

  // Long boards don't fit the screen: hovering near an edge scrolls it.
  useEffect(() => {
    if (!dragActive) return;
    const scroller = scrollParent(rootRef.current);
    if (!scroller) return;
    let frame = 0;
    const tick = () => {
      const current = dragRef.current;
      if (!current?.active) return;
      const isRoot = scroller === document.scrollingElement;
      const top = isRoot ? 0 : scroller.getBoundingClientRect().top;
      const bottom = isRoot ? window.innerHeight : scroller.getBoundingClientRect().bottom;
      const depth =
        current.y < top + SORT_SCROLL_EDGE
          ? current.y - (top + SORT_SCROLL_EDGE)
          : current.y > bottom - SORT_SCROLL_EDGE
            ? current.y - (bottom - SORT_SCROLL_EDGE)
            : 0;
      if (depth !== 0) {
        const speed = Math.max(-1, Math.min(1, depth / SORT_SCROLL_EDGE)) * SORT_SCROLL_MAX_SPEED;
        const before = scroller.scrollTop;
        scroller.scrollTop += speed;
        if (scroller.scrollTop !== before) {
          const over = sortZoneAt(current.x, current.y);
          if (over !== current.over) setDrag({ ...current, over });
        }
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [dragActive, setDrag]);

  const check = useCallback(() => {
    if (finished || pool.length > 0) return;
    const correct = sortCorrectCount(entry, saved);
    const right = sortIsSolved(entry, saved);
    persistDay({
      sort: { placement: saved, attempts: progress.attempts + 1 },
      ...(right ? { solved: true } : {}),
    });
    setVerdict(right ? null : correct);
    if (right) celebrateDailyWin();
  }, [finished, pool.length, entry, saved, progress.attempts, persistDay]);

  const giveUp = useCallback(() => {
    if (finished) return;
    if (!window.confirm(GIVE_UP_CONFIRM)) return;
    persistDay({ givenUp: true });
    setDrag(null);
    setSelected(null);
    setVerdict(null);
  }, [finished, persistDay, setDrag]);

  const renderChip = (name: string) => {
    const dragging = dragActive && drag?.name === name;
    const state = solved
      ? " is-correct"
      : givenUp
        ? saved[name] === solution[name]
          ? " is-correct"
          : " is-revealed"
        : "";
    return (
      <button
        key={name}
        type="button"
        className={
          "daily-sort-chip" +
          state +
          (selected === name ? " is-selected" : "") +
          (dragging ? " is-dragging" : "")
        }
        disabled={finished}
        aria-pressed={finished ? undefined : selected === name}
        aria-keyshortcuts={finished ? undefined : "0 1 2 3 4 5"}
        onPointerDown={(e) => onPointerDown(name, e)}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={cancelDrag}
        onLostPointerCapture={cancelDrag}
        onClick={(e) => onChipClick(name, e)}
        onKeyDown={(e) => onChipKeyDown(name, e)}
      >
        {name}
      </button>
    );
  };

  const zoneClass = (base: string, zone: SortZone) =>
    base +
    (dragActive && drag?.over === zone ? " is-over" : "") +
    (selected !== null || dragActive ? " is-targetable" : "");

  return (
    <div className={"daily-sort" + (dragActive ? " is-dragging" : "")} ref={rootRef}>
      <p className="daily-sort-instruction">{entry.instruction}</p>

      {!finished && (
        <>
          <p className="daily-sort-lead">
            Přetáhni každou položku do správné skupiny – nebo ji ťukni a pak ťukni na skupinu.
          </p>

          <section
            className={zoneClass("daily-sort-pool", "pool")}
            data-sort-zone="pool"
            onClick={() => onZoneClick("pool")}
          >
            <button
              type="button"
              className="daily-sort-zone-head"
              disabled={!selected}
              aria-label={selected ? `Vrátit ${selected} mezi nezařazené` : undefined}
            >
              Nezařazené
              <span className="daily-sort-zone-count">
                {pool.length} / {total}
              </span>
            </button>
            {pool.length > 0 ? (
              <div className="daily-sort-chips">{pool.map(renderChip)}</div>
            ) : (
              <p className="daily-sort-empty">Vše je zařazeno.</p>
            )}
          </section>
        </>
      )}

      <div className="daily-sort-bins">
        {entry.categories.map((category, index) => {
          const items = pile.filter((name) => placement[name] === index);
          return (
            <section
              key={`${category.name}-${index}`}
              className={zoneClass("daily-sort-bin", index)}
              data-sort-zone={finished ? undefined : index}
              onClick={finished ? undefined : () => onZoneClick(index)}
            >
              <button
                type="button"
                className="daily-sort-zone-head"
                disabled={!selected}
                aria-label={selected ? `Zařadit ${selected} do: ${category.name}` : undefined}
              >
                {!finished && (
                  <span className="daily-sort-zone-key" aria-hidden="true">
                    {index + 1}
                  </span>
                )}
                <span className="daily-sort-zone-name">{category.name}</span>
                <span className="daily-sort-zone-count">{items.length}</span>
              </button>
              <div className="daily-sort-chips">{items.map(renderChip)}</div>
            </section>
          );
        })}
      </div>

      {finished ? (
        <div className={"daily-result" + (solved ? "" : " daily-result-lost")}>
          {givenUp && <div className="daily-result-tag">Vzdáno</div>}
          <div className="daily-result-name">
            {solved
              ? `Správně na ${progress.attempts}. pokus`
              : `Správně jsi zařadil ${sortCorrectCount(entry, saved)} z ${total}`}
          </div>
          <p className="daily-result-desc">{entry.instruction}</p>
        </div>
      ) : (
        <>
          {verdict !== null && (
            <div className="daily-organize-verdict">
              Správně zařazeno {verdict} z {total}. Zkus to znovu.
            </div>
          )}

          <div className="daily-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={check}
              disabled={pool.length > 0}
            >
              {pool.length > 0 ? `Zbývá zařadit ${pool.length}` : "Zkontrolovat"}
            </button>
            {progress.attempts > 0 && (
              <span className="daily-organize-attempts">Pokusů: {progress.attempts}</span>
            )}
            <button type="button" className="btn btn-danger daily-give-up-btn" onClick={giveUp}>
              Vzdát se
            </button>
          </div>
        </>
      )}

      {dragActive &&
        drag &&
        createPortal(
          <div
            className="daily-sort-chip daily-sort-ghost"
            style={{
              left: drag.x - drag.grabX,
              top: drag.y - drag.grabY,
              width: drag.width,
            }}
            aria-hidden="true"
          >
            {drag.name}
          </div>,
          document.body,
        )}
    </div>
  );
}

/* ── Join (glue two answers together over their overlap) ─────────── */

interface JoinCardProps {
  entry: DailyJoin;
  dayProgress: DailyDayProgress;
  solved: boolean;
  persistDay: (patch: Partial<DailyDayProgress>) => void;
  resetKey: string;
}

function JoinCard({ entry, dayProgress, solved, persistDay, resetKey }: JoinCardProps) {
  const [input, setInput] = useState("");
  const [bad, setBad] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const givenUp = dayProgress.givenUp;
  const finished = solved || givenUp;
  const hints = Math.min(dayProgress.hintsRevealed, JOIN_HINT_COUNT);
  const mask = useMemo(
    () => (hints > 0 ? joinAnswerMask(entry, hints > 1) : null),
    [entry, hints],
  );

  useEffect(() => {
    setInput("");
    setBad(false);
    inputRef.current?.focus();
  }, [resetKey, finished]);

  const submit = useCallback(
    (value: string) => {
      if (finished || !value.trim()) return;
      if (joinIsCorrect(entry, value)) {
        persistDay({ solved: true });
        celebrateDailyWin();
        setInput("");
        return;
      }
      setBad(true);
      setTimeout(() => setBad(false), 380);
    },
    [entry, finished, persistDay],
  );

  const revealHint = useCallback(() => {
    if (finished || hints >= JOIN_HINT_COUNT) return;
    persistDay({ hintsRevealed: hints + 1 });
  }, [finished, hints, persistDay]);

  const giveUp = useCallback(() => {
    if (finished) return;
    if (!window.confirm(GIVE_UP_CONFIRM)) return;
    persistDay({ givenUp: true });
    setInput("");
  }, [finished, persistDay]);

  return (
    <div className="daily-join">
      <p className="daily-join-lead">
        Uhodni obě odpovědi a spoj je v jednu – konec první se překrývá se začátkem druhé.
      </p>

      <div className="daily-join-clues">
        {entry.clues.map((clue, i) => (
          <Fragment key={i}>
            {i > 0 && (
              <span className="daily-join-plus" aria-hidden="true">
                +
              </span>
            )}
            <div className="daily-join-clue">
              <span className="daily-join-clue-index">{i + 1}</span>
              <p className="daily-join-clue-text">{clue}</p>
            </div>
          </Fragment>
        ))}
      </div>

      {finished ? (
        <div className={"daily-result" + (givenUp ? " daily-result-lost" : "")}>
          {givenUp && <div className="daily-result-tag">Vzdáno</div>}
          <div className="daily-result-name">{entry.answer}</div>
        </div>
      ) : (
        <>
          {mask && (
            <div className="daily-join-mask" aria-label={`Nápověda: ${mask.join(" ")}`}>
              {mask.map((word, i) => (
                <span key={i} className="daily-join-mask-word">
                  {word}
                </span>
              ))}
            </div>
          )}

          <form
            className="daily-input-row"
            onSubmit={(e) => {
              e.preventDefault();
              submit(input);
            }}
          >
            <input
              ref={inputRef}
              className={"input" + (bad ? " input-bad" : "")}
              placeholder="Napiš spojenou odpověď…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="words"
              spellCheck={false}
              aria-label="Odpověď"
            />
          </form>

          <div className="daily-actions">
            {hints < JOIN_HINT_COUNT && (
              <button type="button" className="btn daily-hint-btn" onClick={revealHint}>
                {hints === 0 ? "Zobrazit počet písmen" : "Zobrazit počáteční písmena"}
              </button>
            )}
            <button type="button" className="btn btn-danger daily-give-up-btn" onClick={giveUp}>
              Vzdát se
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** Whether today's daily is still unplayed (not solved, not given up) – used for the header glow. */
export function isTodayDailyUnplayed(): boolean {
  const store = loadDailyProgress();
  const key = dateKeyForOffset(todayOffset());
  const day = getDailyDayProgress(store, key);
  return !day.solved && !day.givenUp;
}
